// The row under the last lane: tap it for a new track, or drop material on
// it (a piece of this project, one of your own clips, or audio files from
// the desktop) and that material gets a track of its own — Live's "drop into
// the empty space below".

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { MATERIAL_DRAG_TYPE, MINE_DRAG_TYPE } from "@web-shared/lib/drag-types";
import { DROP_NEW_TRACK_ATTR, LIFT_DROP, LIFT_LEAVE, LIFT_OVER, type LiftEvent } from "@web-shared/lib/lift-drag";
import { cn } from "@web-shared/lib/utils";

export function NewTrackRow({
  onClick,
  onDropMaterial,
  onDropMine,
  onDropMineMidi,
  onDropFiles,
  children,
  className,
}: {
  onClick: () => void;
  /** A piece of the room's own material (a clip id). */
  onDropMaterial: (clipId: string) => void;
  /** One of the producer's library clips (its id). */
  onDropMine: (libraryClipId: string) => void;
  /** A MIDI idea from the library, let go below the lanes: a new MIDI lane with it on. */
  onDropMineMidi?: (libraryMidiId: string) => void;
  /** Audio files from outside the page — the whole drop, read during its event. */
  onDropFiles?: (dt: DataTransfer) => void;
  /** The label; "New track" when absent. */
  children?: ReactNode;
  className?: string;
}) {
  const [over, setOver] = useState(false);
  // A thumb's drag (lib/lift-drag.ts) arrives as DOM events; the handlers
  // are read through a ref so the listeners register once.
  const ref = useRef<HTMLButtonElement>(null);
  const lift = useRef({ onDropMaterial, onDropMine, onDropMineMidi });
  lift.current = { onDropMaterial, onDropMine, onDropMineMidi };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const on = (e: Event) => {
      const { type, detail } = e as LiftEvent;
      if (type === LIFT_OVER) setOver(true);
      else if (type === LIFT_LEAVE) setOver(false);
      else {
        setOver(false);
        if ("material" in detail.payload) lift.current.onDropMaterial(detail.payload.material);
        else if ("mine" in detail.payload) lift.current.onDropMine(detail.payload.mine);
        else if ("mineMidi" in detail.payload) lift.current.onDropMineMidi?.(detail.payload.mineMidi);
      }
    };
    for (const t of [LIFT_OVER, LIFT_LEAVE, LIFT_DROP]) el.addEventListener(t, on);
    return () => {
      for (const t of [LIFT_OVER, LIFT_LEAVE, LIFT_DROP]) el.removeEventListener(t, on);
    };
  }, []);
  const accepts = (types: readonly string[]) =>
    types.includes(MATERIAL_DRAG_TYPE) || types.includes(MINE_DRAG_TYPE) || (onDropFiles !== undefined && types.includes("Files"));
  return (
    <button
      ref={ref}
      type="button"
      {...{ [DROP_NEW_TRACK_ATTR]: "" }}
      onClick={onClick}
      onDragOver={(e) => {
        if (!accepts(e.dataTransfer.types)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const material = e.dataTransfer.getData(MATERIAL_DRAG_TYPE);
        const mine = e.dataTransfer.getData(MINE_DRAG_TYPE);
        if (!material && !mine) {
          if (onDropFiles && e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            onDropFiles(e.dataTransfer);
          }
          return;
        }
        e.preventDefault();
        if (material) onDropMaterial(material);
        else onDropMine(mine);
      }}
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-3 text-sm text-muted-foreground transition-colors",
        over
          ? "border-primary bg-[color-mix(in_srgb,var(--primary)_10%,transparent)] text-foreground"
          : "border-panelline hover:border-foreground/30 hover:text-foreground",
        className,
      )}
    >
      <Plus className="size-4" />
      {over ? "Drop for a new track" : (children ?? "New track")}
    </button>
  );
}
